import CustomTextField from '../../Input/CustomTextField';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import styled from 'styled-components';
import TipsIcon from 'components/TipsIcon';
import { useEffect, useState } from 'react';
import { getClusterByWorkflow, produce, publish } from 'utility/general';
import CustomButton from 'components/CustomInput/Button';
import api from 'api/call';
import HospitalOrInstitutions from 'components/Global/HospitalOrInstitutions';
import ClusterSelection from 'components/ClusterSelection';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';
import { getStore } from 'store';
import useDialog from 'components/ConfirmDialog/useDialog';
import { UPDATE_APPLICATION_FROM_CLUSTER } from 'utility/frontendMessage';
import { APPLICANT } from 'utility/lookup';

const SSection = styled.div`
    margin-top: 15px;
`;

const SDividedLayout = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-top: 15px;
`;

const SInputItem = styled.div`
    width: 32%;
`;

const SLargeLayout = styled.div`
    width: 66%;
`;

const LeadPrincipalInfo = ({
    onDataUpdate = (data) => null,
    onValidationUpdate = (validationObject) => null,
    onItemUpdate = (key, value) => null,
    defaultData = {},
    forceErrorDisplay = false,
    disableOnPermission = false,
    piTableData,
    isFormCreate,
    isHighlight = () => {},
    clonedWorkflow,
}) => {
    const [data, setData] = useState({});
    const [isEmailLoading, setEmailLoading] = useState(false);
    const [validation, setValidation] = useState({});
    const [allowClusterInHospital, setAllowClusterInHospital] = useState([]);
    const [allowWorkflow] = useState([]);
    const [allowCluster, setAllowCluster] = useState([]);
    const [forceProfileErrorShow, setForceProfileErrorShow] = useState(false);
    const [isHospitalHover, setHospitalHover] = useState(false);
    const showDialog = useDialog();

    // initialize the study-info postData if edit data exist
    useEffect(() => {
        const { applicationDetailPostData } = getStore().getState();
        const postData = applicationDetailPostData.postData?.applicationForm ?? {};
        setData(postData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const clusters = getClusterByWorkflow(defaultData?.workflow ?? clonedWorkflow, true);
        setAllowCluster(clusters);
    }, [defaultData?.workflow, clonedWorkflow]);

    useEffect(() => {
        onValidationUpdate(validation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validation]);

    const getFilteredWorkflowChoice = () => {
        if (allowWorkflow.length === 0) {
            return workflowChoice;
        }

        return workflowChoice.filter((item) => {
            return allowWorkflow.includes(item.key);
        });
    };

    const getData = (key) => {
        return data[key] ?? defaultData[key];
    };

    const workflowChoice = [
        { key: 'I', display: 'Central IRB' },
        { key: 'H', display: 'HKU / HA HKW IRB' },
        { key: 'N', display: 'Joint CUHK-NTEC CREC' },
        // { key: 'M', display: 'Multi-cluster' },
    ];

    const onChange = async (e) => {
        const { name, value } = e.target;

        if (name === 'cluster') {
            setAllowClusterInHospital([value]);
        }

        if (name === 'workflow') {
            if (piTableData?.length > 0) {
                const isConfirm = await showDialog({
                    description: UPDATE_APPLICATION_FROM_CLUSTER,
                });

                if (!isConfirm) {
                    return;
                }

                publish({ topic: 'clearPiData' });
            }

            const clusters = getClusterByWorkflow(value, true);
            setAllowCluster(clusters);
        }

        setData((prevData) => {
            const dataToUpdate = produce(prevData, (draft) => {
                draft[name] = value;

                if (name === 'cluster') {
                    draft['institution'] = '';
                }

                if (name === 'workflow') {
                    draft['cluster'] = '';
                    draft['institution'] = '';
                }
            });

            return dataToUpdate;
        });

        onItemUpdate(name, value);
    };

    useEffect(() => {
        onDataUpdate(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const onProfileLoad = async () => {
        if (!validation['email']) {
            return;
        }

        try {
            setEmailLoading(true);

            const email = data?.email ?? defaultData.email ?? '';

            const res = await api.user.getUserByEmail(email, APPLICANT);
            const { contact, firstName, surname, title } = res.data.result;

            const dataToUpdate = produce(data, (draft) => {
                draft['contact'] = contact;
                // draft['institution'] = institution;
                draft['firstName'] = firstName;
                draft['surname'] = surname;
                draft['title'] = title;
                draft['email'] = res.data.result.email;
                // draft['cluster'] = cluster;
            });

            setData(dataToUpdate); // set local component data
            onDataUpdate(dataToUpdate); // notify parent to update the data
            setForceProfileErrorShow(true);
        } catch (error) {
            console.log('173307 LeadPrincipalinfo.jsx error', error);
        }

        setEmailLoading(false);
    };

    const checkValidation = (type, isValid) => {
        setValidation(
            produce((draft) => {
                draft[type] = isValid;
            }),
        );
    };

    const isHoverTips = () => {
        return !!getData('institution') || isHospitalHover;
    };

    return (
        <>
            <SSection style={{ display: 'flex', alignItems: 'center' }}>
                <div>
                    <span style={{ background: isHighlight(['p1ScientificTitle']) ? 'yellow' : 'initial' }}>
                        Applying IRB / REC
                    </span>
                    <TipsIcon
                        messages={[
                            'Central IRB: For all eligible HA staff applying approval in HA sites',
                            'Joint CUHK-NTEC CREC: For all eligible CUHK and/or HA NTEC staff/students applying approval in CUHK/HA NTEC sites',
                            'HKU/HA HKW IRB: For all eligible HKU and /or HA HKWC staff/students applying approval in HKU and/or HA HKWC sites',
                        ]}
                    />
                    :
                </div>

                <CustomOutlineSelect
                    style={{ marginLeft: 15 }}
                    value={getData('workflow') ?? clonedWorkflow}
                    forceErrorDisplay={forceErrorDisplay}
                    name="workflow"
                    showEmptyChoice
                    isRequired
                    disabled={!isFormCreate}
                    isValidValue={(isValid) => {
                        checkValidation('workflow', isValid);
                    }}
                    choices={getFilteredWorkflowChoice()}
                    onChange={onChange}
                />
            </SSection>
            <SSection>
                <CustomTextField
                    label="Scientific title"
                    name="p1ScientificTitle"
                    debounceChange
                    forceErrorDisplay={forceErrorDisplay}
                    maxLength={500}
                    disableOnPermission={disableOnPermission || !isFormCreate}
                    validators={['isRequired']}
                    isValidValue={(isValid) => {
                        checkValidation('p1ScientificTitle', isValid);
                    }}
                    value={getData('p1ScientificTitle')}
                    onChange={onChange}
                />
            </SSection>
            <SSection>
                <TextSubHeader>
                    <span
                        style={{
                            background: isHighlight([
                                'email',
                                'contact',
                                'title',
                                'surname',
                                'firstName',
                                'position',
                                'department',
                                'cluster',
                                'institution',
                            ])
                                ? 'yellow'
                                : 'initial',
                        }}
                    >
                        Coordinating Investigator (Applicant)
                    </span>{' '}
                    <TipsIcon
                        messages={[
                            `- To complete the 'Study Info' of the Application Form and provide study-based application documents`,
                            `- To coordinate with Site PIs to submit the Initial Application for review`,
                            `- To submit study-based Post-approval Applications (e.g. Final Report, SUSAR Report, Study-based Amendment, CTC/CTI etc.)`,
                        ]}
                    />
                </TextSubHeader>
            </SSection>
            <SDividedLayout>
                <SLargeLayout>
                    <div style={{ display: 'flex' }}>
                        <div style={{ flexGrow: 1, paddingRight: 20 }}>
                            <CustomTextField
                                label="Email"
                                name="email"
                                disableOnPermission={disableOnPermission}
                                debounceChange
                                maxLength={255}
                                forceErrorDisplay={forceErrorDisplay || forceProfileErrorShow}
                                validators={['isRequired', 'isEmail']}
                                isValidValue={(isValid) => {
                                    checkValidation('email', isValid);
                                }}
                                value={getData('email')}
                                disabled={isEmailLoading}
                                onChange={onChange}
                                showErrorWhenOnBlur
                            />
                        </div>
                        <div>
                            <CustomButton
                                loading={isEmailLoading}
                                disableOnPermission={disableOnPermission}
                                style={{ height: 37.6 }}
                                onClick={onProfileLoad}
                                disabled={!defaultData.email && !data?.email}
                            >
                                Load Profile
                            </CustomButton>
                        </div>
                    </div>
                </SLargeLayout>
                <SInputItem>
                    <CustomTextField
                        forceErrorDisplay={forceErrorDisplay || forceProfileErrorShow}
                        disableOnPermission={disableOnPermission}
                        validators={['isRequired', 'disableAlphabet']}
                        maxLength={255}
                        debounceChange
                        isValidValue={(isValid) => {
                            checkValidation('contact', isValid);
                        }}
                        label="Contact Number"
                        name="contact"
                        onChange={onChange}
                        value={getData('contact')}
                        test
                    />
                </SInputItem>
            </SDividedLayout>
            <SDividedLayout>
                <SInputItem>
                    <CustomTextField
                        forceErrorDisplay={forceErrorDisplay || forceProfileErrorShow}
                        label="Title"
                        name="title"
                        disableOnPermission={disableOnPermission}
                        debounceChange
                        maxLength={255}
                        validators={['isRequired', 'disableChinese']}
                        isValidValue={(isValid) => {
                            checkValidation('title', isValid);
                        }}
                        value={getData('title')}
                        tooltip={'e.g. Dr/Mr/Mrs/Ms/Prof.'}
                        onChange={onChange}
                    />
                </SInputItem>
                <SInputItem>
                    <CustomTextField
                        validators={['isRequired', 'disableChinese']}
                        disableOnPermission={disableOnPermission}
                        isValidValue={(isValid) => {
                            checkValidation('surname', isValid);
                        }}
                        maxLength={255}
                        debounceChange
                        label="Surname"
                        name="surname"
                        value={getData('surname')}
                        forceErrorDisplay={forceErrorDisplay || forceProfileErrorShow}
                        onChange={onChange}
                    />
                </SInputItem>
                <SInputItem>
                    <CustomTextField
                        validators={['isRequired', 'disableChinese']}
                        disableOnPermission={disableOnPermission}
                        isValidValue={(isValid) => {
                            checkValidation('firstName', isValid);
                        }}
                        label="First Name"
                        debounceChange
                        name="firstName"
                        maxLength={255}
                        forceErrorDisplay={forceErrorDisplay || forceProfileErrorShow}
                        value={getData('firstName')}
                        onChange={onChange}
                    />
                </SInputItem>
            </SDividedLayout>
            <SDividedLayout>
                <SInputItem>
                    <CustomTextField
                        label="Position"
                        name="position"
                        debounceChange
                        disableOnPermission={disableOnPermission}
                        maxLength={255}
                        validators={['isRequired']}
                        forceErrorDisplay={forceErrorDisplay}
                        isValidValue={(isValid) => {
                            checkValidation('position', isValid);
                        }}
                        value={getData('position')}
                        tooltip={
                            'Please fill in the position relevant to the hospital/institution which you have selected below.'
                        }
                        onChange={onChange}
                    />
                </SInputItem>

                <SInputItem>
                    <SDividedLayout style={{ marginTop: 0 }}>
                        <div style={{ flex: 1, paddingRight: 10 }}>
                            <CustomTextField
                                label="Department"
                                name="department"
                                disableOnPermission={disableOnPermission}
                                debounceChange
                                validators={['isRequired']}
                                maxLength={255}
                                forceErrorDisplay={forceErrorDisplay}
                                isValidValue={(isValid) => {
                                    checkValidation('department', isValid);
                                }}
                                value={getData('department')}
                                onChange={onChange}
                            />
                        </div>
                        <div style={{ width: 120 }}>
                            <ClusterSelection
                                isRequired
                                disableOnPermission={disableOnPermission}
                                allowClusters={allowCluster}
                                isValidValue={(isValid) => {
                                    checkValidation('cluster', isValid);
                                }}
                                value={getData('cluster')}
                                forceErrorDisplay={forceErrorDisplay}
                                onChange={(e) => {
                                    onChange(e);
                                }}
                            />
                        </div>
                    </SDividedLayout>
                </SInputItem>
                <SInputItem>
                    <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <HospitalOrInstitutions
                            label="Hospital / Institution"
                            disableOnPermission={disableOnPermission}
                            name="institution"
                            validators={['isRequired']}
                            isValidValue={(isValid) => {
                                checkValidation('institution', isValid);
                            }}
                            forceErrorDisplay={forceErrorDisplay}
                            allowClusters={allowClusterInHospital}
                            disabled={!getData('cluster')}
                            disableHKU={getData('workflow') === 'N'}
                            disableCUHK={getData('workflow') === 'H'}
                            isAuthorizedEmail={true} // must be authorized people to create the team member form
                            hospitalOrInstitution={getData('institution')}
                            setHospitalOrInstitution={onChange}
                            onFocus={() => setHospitalHover(true)}
                            onBlur={() => setHospitalHover(false)}
                        />
                        <div
                            style={{
                                paddingLeft: 5,
                                position: 'absolute',
                                top: isHoverTips() ? -4 : 16,
                                left: isHoverTips() ? 106 : 140,
                            }}
                        >
                            <TipsIcon
                                title={
                                    <div>
                                        <div>
                                            Central IRB: Coordinating Investigator shall be an employee/appointee of HA
                                        </div>
                                        <div style={{ marginTop: 15 }}>
                                            HKU/HA HKW IRB: Coordinating Investigator shall be an
                                            employee/appointee/student of the HKU or HA HKWC
                                        </div>
                                        <div style={{ marginTop: 15 }}>
                                            Joint CUHK-NTEC CREC: Coordinating Investigator shall be an
                                            employee/appointee/student of the CUHK or HA NTEC
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                    </div>
                </SInputItem>
            </SDividedLayout>
        </>
    );
};

export default LeadPrincipalInfo;
